export interface EventDescriptionSource {
  markdown_description?: string | null;
  plain_description?: string | null;
}

type LexicalNode = {
  type?: string;
  text?: string;
  children?: LexicalNode[];
  format?: number | string;
  tag?: string;
  listType?: string;
  url?: string;
};

const BLOCK_NODE_TYPES = new Set(['paragraph', 'quote', 'heading', 'list', 'listitem']);
const TEXT_FORMAT_BOLD = 1;
const TEXT_FORMAT_ITALIC = 1 << 1;
const TEXT_FORMAT_STRIKETHROUGH = 1 << 2;
const TEXT_FORMAT_UNDERLINE = 1 << 3;
const TEXT_FORMAT_CODE = 1 << 4;

const normaliseWhitespace = (value: string): string =>
  value
    .replace(/\r/g, '')
    .replace(/[ \t]+/g, ' ')
    .trim();
const trimRight = (value: string): string => value.replace(/\s+$/g, '');

const isLexicalJson = (value: string): boolean => {
  try {
    const parsed = JSON.parse(value);
    return !!parsed && typeof parsed === 'object' && 'root' in parsed;
  } catch {
    return false;
  }
};

const getNodeFormat = (node: LexicalNode): number => {
  const format = typeof node.format === 'string' ? Number(node.format) : node.format;
  return Number.isFinite(format) ? Number(format) : 0;
};

const escapeMarkdownText = (value: string): string => value.replace(/([\\`*_\[\]])/g, '\\$1');

const applyTextFormatting = (value: string, format: number): string => {
  let output = escapeMarkdownText(value);
  if (!output) return output;
  if (format & TEXT_FORMAT_CODE) {
    output = `\`${output.replace(/`/g, '\\`')}\``;
  }
  if (format & TEXT_FORMAT_BOLD) {
    output = `**${output}**`;
  }
  if (format & TEXT_FORMAT_ITALIC) {
    output = `*${output}*`;
  }
  if (format & TEXT_FORMAT_STRIKETHROUGH) {
    output = `~~${output}~~`;
  }
  if (format & TEXT_FORMAT_UNDERLINE) {
    output = `<u>${output}</u>`;
  }
  return output;
};

const appendLexicalText = (node: LexicalNode, output: string[]): void => {
  if (node.type === 'linebreak') {
    output.push('\n');
    return;
  }

  if (typeof node.text === 'string') {
    output.push(node.text);
  }

  if (!Array.isArray(node.children) || node.children.length === 0) {
    return;
  }

  node.children.forEach((child, index) => {
    appendLexicalText(child, output);
    if (BLOCK_NODE_TYPES.has(child.type || '') && index < node.children!.length - 1) {
      output.push('\n');
    }
  });
};

const lexicalJsonToPlainText = (value: string): string => {
  try {
    const parsed = JSON.parse(value) as { root?: LexicalNode };
    if (!parsed.root) return value;
    const output: string[] = [];
    appendLexicalText(parsed.root, output);
    return output.join('');
  } catch {
    return value;
  }
};

const renderMarkdownBlocks = (children: LexicalNode[] | undefined): string => {
  if (!Array.isArray(children) || children.length === 0) return '';
  return children
    .map((child) => renderLexicalNodeAsMarkdown(child))
    .filter((value) => value.length > 0)
    .join('\n\n');
};

const renderInlineChildren = (children: LexicalNode[] | undefined): string => {
  if (!Array.isArray(children) || children.length === 0) return '';
  return children.map((child) => renderLexicalNodeAsMarkdown(child)).join('');
};

const renderListItem = (node: LexicalNode, index: number, ordered: boolean): string => {
  const marker = ordered ? `${index + 1}. ` : '- ';
  const content = renderInlineChildren(node.children).trim();
  return trimRight(`${marker}${content}`);
};

const renderLexicalNodeAsMarkdown = (node: LexicalNode): string => {
  switch (node.type) {
    case 'root':
      return renderMarkdownBlocks(node.children);
    case 'paragraph': {
      return renderInlineChildren(node.children).trim();
    }
    case 'linebreak':
      return '  \n';
    case 'text':
      return applyTextFormatting(node.text || '', getNodeFormat(node));
    case 'heading': {
      const level = node.tag?.startsWith('h') ? Number(node.tag.substring(1)) : 1;
      const prefix = '#'.repeat(level >= 1 && level <= 6 ? level : 1);
      return `${prefix} ${renderInlineChildren(node.children).trim()}`.trim();
    }
    case 'quote': {
      return renderInlineChildren(node.children)
        .split('\n')
        .map((line) => trimRight(`> ${line}`))
        .join('\n');
    }
    case 'list': {
      const ordered = node.listType === 'number';
      return (node.children || []).map((child, index) => renderListItem(child, index, ordered)).join('\n');
    }
    case 'listitem':
      return renderInlineChildren(node.children).trim();
    case 'link': {
      const label = renderInlineChildren(node.children).trim() || node.url || '';
      return node.url ? `[${label}](${node.url})` : label;
    }
    default:
      return renderInlineChildren(node.children);
  }
};

const toPlainText = (value: string): string => {
  const lexicalExpanded = isLexicalJson(value) ? lexicalJsonToPlainText(value) : value;
  return normaliseWhitespace(
    lexicalExpanded
      .replace(/<[^>]+>/g, ' ')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, ' $1 ')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, ' $1 ')
      .replace(/(```[\s\S]*?```)/g, ' ')
      .replace(/`([^`]+)`/g, ' $1 ')
      .replace(/(\*\*|__)(.*?)\1/g, ' $2 ')
      .replace(/(\*|_)(.*?)\1/g, ' $2 ')
      .replace(/~~(.*?)~~/g, ' $1 ')
      .replace(/^\s{0,3}(#{1,6}|>|- |\* |\+ |\d+\. )/gm, '')
      .replace(/\n{2,}/g, '\n'),
  );
};

export const deriveEventPlainDescription = (value: EventDescriptionSource): string => {
  const preferredSource = value.markdown_description || value.plain_description || '';
  return toPlainText(preferredSource);
};

export const getPreferredEventTileDescription = (value: EventDescriptionSource): string => {
  if (value.markdown_description) {
    return toPlainText(value.markdown_description);
  }
  if (value.plain_description) {
    return normaliseWhitespace(value.plain_description);
  }
  return '';
};
